import { type FC, useState } from 'react';

import {
  type FocusedInput,
  START_DATE,
  useDatepicker,
} from '@datepicker-react/hooks';
import { clsx } from 'clsx';
import { differenceInDays } from 'date-fns';

import { Month } from './components';
import { DatePickerContext } from './context';
import type { CalendarProps, DateData } from './types';

const Calendar: FC<React.PropsWithChildren<CalendarProps>> = ({
  isDark,
  startDate,
  onSelect,
  endDate,
}) => {
  const [focusedInput, setFocusedInput] = useState<FocusedInput>(START_DATE);

  const handleDateChange = (data: DateData) => {
    if (!data.focusedInput) {
      onSelect({ startDate: data.startDate, endDate: data.endDate });
      setFocusedInput(START_DATE);
    } else {
      onSelect({ startDate: data.startDate, endDate: data.endDate });
      setFocusedInput(data.focusedInput);
    }
  };

  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToNextMonths,
  } = useDatepicker({
    firstDayOfWeek: 0,
    startDate,
    endDate,
    focusedInput,
    onDatesChange: handleDateChange,
    numberOfMonths: 1,
    isDateBlocked: date => differenceInDays(date, new Date()) < 0,
  });

  return (
    <DatePickerContext.Provider
      value={{
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
      }}
    >
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${activeMonths.length}, 1fr)`,
          justifyItems: 'center',
          minHeight: '384px',
        }}
        className={clsx(
          'w-full h-full p-40 rounded-5',
          isDark ? 'bg-base-lighter-dark-mode' : 'bg-white shadow-tooltip',
        )}
      >
        {activeMonths.map(month => (
          <Month
            key={`${month.year}-${month.month}`}
            goToPreviousMonth={goToPreviousMonths}
            firstDayOfWeek={firstDayOfWeek}
            goToNextMonth={goToNextMonths}
            month={month.month}
            year={month.year}
            isDark={isDark}
          />
        ))}
      </section>
    </DatePickerContext.Provider>
  );
};

export default Calendar;
